if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      if (!this.form) return;
      
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.subtotalArr = document.querySelectorAll('.totals__subtotal-value');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.initQuantityPack();

      //Detect sticky
      const observer = new IntersectionObserver(
        ([e]) => e.target.classList.toggle("is-sticky", e.intersectionRatio < 1),
        { threshold: [1] }
      );

      observer.observe(this);
    }

    initQuantityPack() {
      const packRoot = this.form.querySelector('[data-quantity-pack]');
      if (!packRoot) return;

      const qtyInput = packRoot.querySelector('[data-quantity-pack-input]');
      const segments = packRoot.querySelectorAll('[data-quantity-pack-mode]');
      const packSize = parseInt(packRoot.dataset.packSize || '1', 10) || 1;

      const setMode = (mode) => {
        const isPack = mode === 'pack';
        if (qtyInput) qtyInput.value = String(isPack ? packSize : 1);
        segments.forEach((segment) => {
          const active = segment.dataset.quantityPackMode === mode;
          segment.classList.toggle('quantity-pack__segment--active', active);
          segment.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      };

      segments.forEach((segment) => {
        segment.addEventListener('click', () => {
          setMode(segment.dataset.quantityPackMode || 'single');
        });
      });

      setMode('single');
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      
      const submitButton = this.querySelector('[type="submit"]');
      if (submitButton.classList.contains('loading')) return;

      // Check if a size is selected (variant radios)
      // Scope to this form/modal only so we don't read another product instance.
      // Avoid the generic `[data-section-id]` selector — cart-drawer and other
      // sectioned wrappers also carry that attribute, which would let us match
      // an unrelated section above us in the tree.
      const modalContent = this.closest('.product-quick-view-modal__content');
      const productRoot = this.closest('main-product, .main-product, [id^="shopify-section-main-product"]');
      const variantRadios = this.querySelector('variant-radios')
        || modalContent?.querySelector('variant-radios')
        || productRoot?.querySelector('variant-radios');
      
      // For quick view modal, check the modal's fieldsets directly if no variant-radios
      // Otherwise check the variant-radios element
      let hasUnselectedOption = false;
      
      if (variantRadios) {
        const fieldsets = variantRadios.querySelectorAll('fieldset');
        fieldsets.forEach(fieldset => {
          const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
          if (!checkedRadio) {
            hasUnselectedOption = true;
          }
        });
      } else if (modalContent) {
        // Quick view without variant-radios - check fieldsets in the modal selector
        const selectorDiv = modalContent.querySelector('[data-quick-view-selector]');
        if (selectorDiv) {
          const fieldsets = selectorDiv.querySelectorAll('fieldset');
          fieldsets.forEach(fieldset => {
            const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
            if (!checkedRadio) {
              hasUnselectedOption = true;
            }
          });
        }
      }
      
      if (hasUnselectedOption) {
        // Get first unselected option name dynamically
        let optionName = 'option';
        if (variantRadios) {
          const firstUnselectedFieldset = Array.from(variantRadios.querySelectorAll('fieldset')).find(fs => {
            return !fs.querySelector('input[type="radio"]:checked');
          });
          if (firstUnselectedFieldset) {
            const legend = firstUnselectedFieldset.querySelector('legend');
            if (legend) {
              // Option name only: the heading's first text node skips extras like the gift card's grey "AUD"
              const headingText = legend.querySelector('.heading')?.firstChild?.textContent;
              optionName = (headingText || legend.textContent).trim().toLowerCase();
            }
          }
        } else if (modalContent) {
          const selectorDiv = modalContent.querySelector('[data-quick-view-selector]');
          if (selectorDiv) {
            const firstUnselectedFieldset = Array.from(selectorDiv.querySelectorAll('fieldset')).find(fs => {
              return !fs.querySelector('input[type="radio"]:checked');
            });
            if (firstUnselectedFieldset) {
              const legend = firstUnselectedFieldset.querySelector('legend');
              if (legend) {
                optionName = legend.textContent.trim().toLowerCase();
              }
            }
          }
        }

        // Size (or unknown) keeps the translated string; other options (e.g. gift card Amount) name themselves
        const isSizeOption = optionName === 'option' || optionName.startsWith('size');
        this.handleErrorMessage(
          isSizeOption
            ? (window.variantStrings?.selectSize || 'Select a size before adding to cart')
            : `Select ${/^[aeiou]/.test(optionName) ? 'an' : 'a'} ${optionName} before adding to cart`
        );
        return;
      }

      this.handleErrorMessage();
      if (this.cartDrawer && typeof this.cartDrawer.setActiveElement === 'function') {
        this.cartDrawer.setActiveElement(document.activeElement);
      }

      submitButton.setAttribute('aria-disabled', true);
      submitButton.disabled = true;

      // Defer loading state (spinner) to avoid flicker on fast add-to-cart responses
      let loadingTimeout = setTimeout(function() {
        loadingTimeout = null;
        submitButton.classList.add('loading');
        var spinner = submitButton.closest('product-form').querySelector('.product-form__spinner .icon-spinner');
        if (spinner) spinner.classList.remove('hidden');
      }, 200);

      const clearLoading = () => {
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
          loadingTimeout = null;
        }
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
        submitButton.disabled = false;
        const spinner = this.querySelector('.product-form__spinner .icon-spinner');
        if (spinner) spinner.classList.add('hidden');
      };

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      try {
        const response = await fetch(`${routes.cart_add_url}`, config);
        const data = await response.json();

        if (data.status) {
          let errorMessage = data.description;
          if (errorMessage && errorMessage.toLowerCase().includes('maximum quantity')) {
            errorMessage = "That's all we have available right now – the last one is in your cart!";
          }
          this.handleErrorMessage(errorMessage);
          clearLoading();
          return;
        }

        // Cart changed: clear the cached cart so the drawer reads fresh data
        invalidateCartCache();

        const isFreeGiftAdd = this.dataset.freeGift === 'true';
        const quickViewModal = this.closest('product-quick-view-modal');
        const cartDrawerElement = document.getElementById('cart-drawer');
        const isCartDrawerAlreadyOpen = Boolean(
          this.cartDrawer &&
          (this.cartDrawer.classList.contains('active') || cartDrawerElement?.classList.contains('active'))
        );

        if (isCartDrawerAlreadyOpen && this.cartDrawer?.renderSection) {
          await this.cartDrawer.renderSection();
          clearLoading();
          document.querySelector('free-gift-drawer')?.close?.();

          if (quickViewModal && typeof quickViewModal.close === 'function') {
            quickViewModal.close();
          }

          if (isFreeGiftAdd) {
            document.dispatchEvent(new CustomEvent('cart:updated', {
              bubbles: true,
              detail: { giftMaintenanceHandled: true }
            }));
          }

          return;
        }

        const cartDrawerSection = document.querySelector('cart-drawer[data-section-id]');
        const sectionId = cartDrawerSection?.dataset.sectionId || 'header';

        const sectionResponse = await fetch(`/?section_id=${sectionId}`, {
          method: 'get',
          'Content-Type': 'application/json'
        });
        const dataText = await sectionResponse.text();
        const doc = parseHTML(dataText);

        const updatedCart = doc.getElementById('cart-drawer-items');
        const cartItemsContainer = document.getElementById('cart-drawer-items');
        if (updatedCart && cartItemsContainer) {
          cartItemsContainer.innerHTML = updatedCart.innerHTML;
        }

        const updatedSubtotal = doc.querySelector('.totals__subtotal-value');
        if (updatedSubtotal && this.subtotalArr) {
          this.subtotalArr.forEach(function(el) {
            el.innerHTML = updatedSubtotal.innerHTML;
          });
        }

        const updatedCartIcon = doc.getElementById('icon-cart');
        const cartIcon = document.getElementById('icon-cart');
        if (updatedCartIcon && cartIcon) {
          cartIcon.innerHTML = updatedCartIcon.innerHTML;
        }

        if (this.cartDrawer && this.cartDrawer.open) {
          await this.cartDrawer.open(true);
        } else if (this.cartDrawer) {
          await this.cartDrawer.open();
        }

        if (quickViewModal && typeof quickViewModal.close === 'function') {
          quickViewModal.close();
        }

        document.querySelector('free-gift-drawer')?.close?.();
        document.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: isFreeGiftAdd ? { giftMaintenanceHandled: true } : undefined
        }));
        clearLoading();
      } catch (e) {
        console.error(e);
        clearLoading();
      }
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
      if (!this.errorMessage) return;

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
